import React from "react";
//#region redux
import { useDispatch } from "react-redux";
import { loadGenericComments } from "store/actions/commonActions";
//#endregion
//#region types
import { GenericCommentViewContainerProps } from "models/propTypes/genericCommentPropTypes";
import { CommentItem } from "models/propTypes/commentViewPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { User } from "models/propTypes/userProfilePropTypes";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import useMountedState from "../useMountedState";
import useDelayedEffect from "../useDelayedEffect";
import CommentsView from "./view";
import AccordionPanel from "../panels/accordianPanel";
import { Skeletons } from "..";
import { useGenericCommentService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const GenericCommentsView: React.FC<GenericCommentViewContainerProps> = (props) => {
  const genericCommentsService = useGenericCommentService();
  const { getMounted } = useMountedState();
  const [comments, setComments] = React.useState<WintSuperkollAPIModel.GenericComment[]>();

  const dispatch = useDispatch();

  useDelayedEffect(
    () => {
      genericCommentsService.getGenericComments(props.parentId, props.type).then((result) => {
        if (getMounted()) {
          const comments = result.items ?? [];
          setComments(comments);

          dispatch(
            loadGenericComments({
              referenceId: props.parentId,
              type: props.type,
              latestComment: result.totalItems > 0 ? comments[0] : undefined,
              commentCount: result.totalItems,
            })
          );
        }
      });
    },
    100,
    [props.parentId, props.type]
  );

  const addComment = (comment: string) => {
    return new Promise((resolve, reject) => {
      if (comment)
        genericCommentsService.addGenericComment(props.parentId, props.type, comment).then((commentId) => {
          resolve(commentId);
          genericCommentsService.getById(commentId).then((comment) => {
            if (getMounted()) {
              dispatch(
                loadGenericComments({
                  referenceId: props.parentId,
                  type: props.type,
                  latestComment: comment,
                  commentCount: (comments?.length ?? 0) + 1,
                })
              );
              setComments((prevState) => [comment, ...(prevState ?? [])]);
            }
          });
        });
      else reject();
    });
  };

  return comments ? (
    <CommentsView
      parentId={props.parentId}
      addCommentHandler={addComment}
      title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Events)}
      addCommentDescription={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.GenericComments.InfoText)}
      totalComments={comments?.length}
      // Generic comments from Wint employees currently arrive with authorId/authorName nulled out by
      // the backend, so they render as the literal "Wint". DEV-25555 changes the API to return the
      // employee's first name instead; once deployed the existing rendering picks it up automatically
      // and no frontend change is needed here.
      comments={comments?.map<CommentItem>((comment) => ({
        id: comment.id,
        message: comment.message ?? "",
        from: new User(comment.authorId, comment.authorName),
        date: comment.created,
        isAutomatic: !comment.isManual,
      }))}
      defaultViewCount={2}
      viewCountIncreaseBy={10}
      defaultExpanded={!props.collapsed && comments.some((comment) => comment.isManual)}
    />
  ) : (
    <AccordionPanel
      title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Events)}
      titleExtraText={<Skeletons.AccordianPanelTitleExtraTextSkeleton />}
    />
  );
};

export default GenericCommentsView;
